import React, { useState, useEffect } from 'react';
import { updateLoggingConfig, getLoggingConfig, LoggingConfig } from '@fitzaa/shared';
import { useTheme } from '../contexts/ThemeContext';

interface LoggingConfigModalProps {
  isVisible?: boolean;
  onClose?: () => void;
}

const LoggingConfigModal: React.FC<LoggingConfigModalProps> = ({ isVisible = false, onClose }) => {
  const { theme, isDarkMode } = useTheme();
  const [config, setConfig] = useState<LoggingConfig>(getLoggingConfig());

  useEffect(() => {
    setConfig(getLoggingConfig());
  }, [isVisible]);

  const handleToggle = (section: keyof LoggingConfig, key: string, value: boolean) => {
    const newConfig = {
      ...config,
      [section]: {
        ...config[section],
        [key]: value
      }
    };

    setConfig(newConfig);
    updateLoggingConfig(newConfig);
  };

  if (!isVisible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 1000,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: theme.colors.surface,
        borderRadius: '12px',
        padding: '32px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '85vh',
        overflow: 'auto',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        border: `1px solid ${theme.colors.border}`
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          paddingBottom: '16px',
          borderBottom: `1px solid ${theme.colors.border}`
        }}>
          <h3 style={{
            margin: 0,
            fontSize: '24px',
            fontWeight: '600',
            color: theme.colors.text
          }}>
            Logging Configuration
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '6px',
              color: theme.colors.textSecondary,
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors.divider;
              e.currentTarget.style.color = theme.colors.text;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = theme.colors.textSecondary;
            }}
          >
            ✕
          </button>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <h4 style={{
            marginBottom: '16px',
            fontSize: '18px',
            fontWeight: '600',
            color: theme.colors.text
          }}>
            Challenge Logging
          </h4>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '12px',
            padding: '12px',
            borderRadius: '8px',
            backgroundColor: theme.colors.divider,
            border: `1px solid ${theme.colors.border}`,
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors.border;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors.divider;
            }}>
            <input
              type="checkbox"
              checked={config.challenges.enabled}
              onChange={(e) => handleToggle('challenges', 'enabled', e.target.checked)}
              style={{
                marginRight: '12px',
                width: '18px',
                height: '18px',
                accentColor: '#3b82f6'
              }}
            />
            <span style={{
              fontSize: '16px',
              fontWeight: '500',
              color: theme.colors.text
            }}>
              Enable Challenge Logging (Master Switch)
            </span>
          </label>

          {config.challenges.enabled && (
            <div style={{ marginLeft: '24px', marginTop: '12px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '8px',
                padding: '8px 12px',
                borderRadius: '6px',
                backgroundColor: theme.colors.divider,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.border;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.divider;
                }}>
                <input
                  type="checkbox"
                  checked={config.challenges.userData}
                  onChange={(e) => handleToggle('challenges', 'userData', e.target.checked)}
                  style={{
                    marginRight: '10px',
                    width: '16px',
                    height: '16px',
                    accentColor: '#3b82f6'
                  }}
                />
                <span style={{ fontSize: '14px', color: theme.colors.text }}>User Data</span>
              </label>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '8px',
                padding: '8px 12px',
                borderRadius: '6px',
                backgroundColor: theme.colors.divider,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.border;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.divider;
                }}>
                <input
                  type="checkbox"
                  checked={config.challenges.eligibility}
                  onChange={(e) => handleToggle('challenges', 'eligibility', e.target.checked)}
                  style={{
                    marginRight: '10px',
                    width: '16px',
                    height: '16px',
                    accentColor: '#3b82f6'
                  }}
                />
                <span style={{ fontSize: '14px', color: theme.colors.text }}>Eligibility Checks</span>
              </label>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '8px',
                padding: '8px 12px',
                borderRadius: '6px',
                backgroundColor: theme.colors.divider,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.border;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.divider;
                }}>
                <input
                  type="checkbox"
                  checked={config.challenges.loading}
                  onChange={(e) => handleToggle('challenges', 'loading', e.target.checked)}
                  style={{
                    marginRight: '10px',
                    width: '16px',
                    height: '16px',
                    accentColor: '#3b82f6'
                  }}
                />
                <span style={{ fontSize: '14px', color: theme.colors.text }}>Loading Operations</span>
              </label>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '8px',
                padding: '8px 12px',
                borderRadius: '6px',
                backgroundColor: theme.colors.divider,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.border;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.divider;
                }}>
                <input
                  type="checkbox"
                  checked={config.challenges.progress}
                  onChange={(e) => handleToggle('challenges', 'progress', e.target.checked)}
                  style={{
                    marginRight: '10px',
                    width: '16px',
                    height: '16px',
                    accentColor: '#3b82f6'
                  }}
                />
                <span style={{ fontSize: '14px', color: theme.colors.text }}>Progress Updates</span>
              </label>
            </div>
          )}
        </div>

        <div style={{ marginBottom: '24px' }}>
          <h4 style={{
            marginBottom: '16px',
            fontSize: '18px',
            fontWeight: '600',
            color: theme.colors.text
          }}>
            Authentication Logging
          </h4>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '12px',
            padding: '12px',
            borderRadius: '8px',
            backgroundColor: theme.colors.divider,
            border: `1px solid ${theme.colors.border}`,
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors.border;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors.divider;
            }}>
            <input
              type="checkbox"
              checked={config.auth.enabled}
              onChange={(e) => handleToggle('auth', 'enabled', e.target.checked)}
              style={{
                marginRight: '12px',
                width: '18px',
                height: '18px',
                accentColor: '#3b82f6'
              }}
            />
            <span style={{
              fontSize: '16px',
              fontWeight: '500',
              color: theme.colors.text
            }}>
              Enable Auth Logging (Master Switch)
            </span>
          </label>

          {config.auth.enabled && (
            <div style={{ marginLeft: '24px', marginTop: '12px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '8px',
                padding: '8px 12px',
                borderRadius: '6px',
                backgroundColor: theme.colors.divider,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.border;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.divider;
                }}>
                <input
                  type="checkbox"
                  checked={config.auth.stateChanges}
                  onChange={(e) => handleToggle('auth', 'stateChanges', e.target.checked)}
                  style={{
                    marginRight: '10px',
                    width: '16px',
                    height: '16px',
                    accentColor: '#3b82f6'
                  }}
                />
                <span style={{ fontSize: '14px', color: theme.colors.text }}>State Changes</span>
              </label>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '8px',
                padding: '8px 12px',
                borderRadius: '6px',
                backgroundColor: theme.colors.divider,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.border;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.divider;
                }}>
                <input
                  type="checkbox"
                  checked={config.auth.userProfile}
                  onChange={(e) => handleToggle('auth', 'userProfile', e.target.checked)}
                  style={{
                    marginRight: '10px',
                    width: '16px',
                    height: '16px',
                    accentColor: '#3b82f6'
                  }}
                />
                <span style={{ fontSize: '14px', color: theme.colors.text }}>User Profile Operations</span>
              </label>
            </div>
          )}
        </div>

        <div style={{ marginBottom: '24px' }}>
          <h4 style={{
            marginBottom: '16px',
            fontSize: '18px',
            fontWeight: '600',
            color: theme.colors.text
          }}>
            General Logging
          </h4>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '12px',
            padding: '12px',
            borderRadius: '8px',
            backgroundColor: theme.colors.divider,
            border: `1px solid ${theme.colors.border}`,
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors.border;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors.divider;
            }}>
            <input
              type="checkbox"
              checked={config.general.enabled}
              onChange={(e) => handleToggle('general', 'enabled', e.target.checked)}
              style={{
                marginRight: '12px',
                width: '18px',
                height: '18px',
                accentColor: '#3b82f6'
              }}
            />
            <span style={{
              fontSize: '16px',
              fontWeight: '500',
              color: theme.colors.text
            }}>
              Enable General Logging (Master Switch)
            </span>
          </label>

          {config.general.enabled && (
            <div style={{ marginLeft: '24px', marginTop: '12px' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '8px',
                padding: '8px 12px',
                borderRadius: '6px',
                backgroundColor: theme.colors.divider,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.border;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.divider;
                }}>
                <input
                  type="checkbox"
                  checked={config.general.errors}
                  onChange={(e) => handleToggle('general', 'errors', e.target.checked)}
                  style={{
                    marginRight: '10px',
                    width: '16px',
                    height: '16px',
                    accentColor: '#3b82f6'
                  }}
                />
                <span style={{ fontSize: '14px', color: theme.colors.text }}>Errors</span>
              </label>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '8px',
                padding: '8px 12px',
                borderRadius: '6px',
                backgroundColor: theme.colors.divider,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.border;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.divider;
                }}>
                <input
                  type="checkbox"
                  checked={config.general.warnings}
                  onChange={(e) => handleToggle('general', 'warnings', e.target.checked)}
                  style={{
                    marginRight: '10px',
                    width: '16px',
                    height: '16px',
                    accentColor: '#3b82f6'
                  }}
                />
                <span style={{ fontSize: '14px', color: theme.colors.text }}>Warnings</span>
              </label>
            </div>
          )}
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          marginTop: '24px',
          paddingTop: '20px',
          borderTop: `1px solid ${theme.colors.border}`
        }}>
          <button
            onClick={() => {
              updateLoggingConfig({
                challenges: { enabled: false, userData: false, eligibility: false, loading: false, progress: false },
                auth: { enabled: true, stateChanges: true, userProfile: true },
                general: { enabled: true, errors: true, warnings: true }
              });
              setConfig(getLoggingConfig());
            }}
            style={{
              padding: '10px 20px',
              border: `1px solid ${theme.colors.border}`,
              borderRadius: '8px',
              backgroundColor: theme.colors.surface,
              color: theme.colors.text,
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors.divider;
              e.currentTarget.style.borderColor = theme.colors.border;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors.surface;
              e.currentTarget.style.borderColor = theme.colors.border;
            }}
          >
            Disable All Challenge Logging
          </button>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              border: '1px solid #3b82f6',
              borderRadius: '8px',
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#2563eb';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#3b82f6';
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoggingConfigModal; 